import React, { useEffect, useState } from "react";
import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Star, MessageSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";

function StarRow({ value, onChange, size = 20, disabled = false }) {
  return (
    <div className="flex items-center gap-1" role="radiogroup" aria-label="Rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={disabled}
          onClick={() => onChange?.(n)}
          className={`transition-transform ${onChange && !disabled ? "hover:scale-110 cursor-pointer" : "cursor-default"}`}
          aria-label={`${n} star`}
          data-testid={`star-${n}`}
        >
          <Star
            size={size}
            className={n <= value ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground/40"}
          />
        </button>
      ))}
    </div>
  );
}

function StarDisplay({ value, size = 14 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={size}
          className={n <= Math.round(value || 0) ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground/40"}
        />
      ))}
    </div>
  );
}

export default function ReviewsSection({ targetUserId, partnerRole, businessName, ownerName }) {
  const { user } = useAuth();
  const { lang } = useLang();
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ avg: null, count: 0 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [name, setName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get(`/reviews/user/${targetUserId}`),
        api.get(`/reviews/summary/${targetUserId}`),
      ]);
      setReviews(r.data || []);
      setSummary(s.data || { avg: null, count: 0 });
    } catch {
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (targetUserId) load(); /* eslint-disable-next-line */ }, [targetUserId]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!rating || rating < 1) {
      toast.error(lang === "hi" ? "कृपया रेटिंग दें" : "Please pick a rating");
      return;
    }
    if (!comment.trim() || comment.trim().length < 3) {
      toast.error(lang === "hi" ? "कृपया कमेंट लिखें" : "Please write a comment");
      return;
    }
    if (!user && !name.trim()) {
      toast.error(lang === "hi" ? "अपना नाम भरें" : "Enter your name");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        target_user_id: targetUserId,
        rating,
        comment: comment.trim(),
        reviewer_name: user ? undefined : name.trim(),
      });
      toast.success(lang === "hi" ? "समीक्षा सबमिट हुई!" : "Review submitted!");
      setRating(0); setComment(""); setName("");
      load();
    } catch (e2) {
      toast.error(formatDetail(e2.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!partnerRole || !["broker", "builder"].includes(partnerRole)) {
    return null; // Reviews only for Broker/Builder partners
  }

  const label = businessName || ownerName || (partnerRole === "builder" ? "Builder" : "Broker");

  return (
    <section className="bg-card border border-border rounded-xl p-5 sm:p-6 mt-6" data-testid="reviews-section">
      <header className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <MessageSquare size={16} className="text-primary" />
          <h3 className="font-bold text-base sm:text-lg">
            {lang === "hi" ? "समीक्षाएँ" : "Reviews"} • {label}
          </h3>
        </div>
        {summary.avg != null && (
          <div className="flex items-center gap-2" data-testid="rating-summary">
            <StarDisplay value={summary.avg} size={16} />
            <span className="text-sm font-bold">{summary.avg}</span>
            <span className="text-xs text-muted-foreground">({summary.count})</span>
          </div>
        )}
      </header>

      {/* Submit form */}
      <form onSubmit={submit} className="p-4 rounded-lg bg-muted/40 border border-border/60 mb-5" data-testid="review-form">
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">
          {lang === "hi" ? "अपनी समीक्षा दें" : "Leave a review"}
        </div>
        <StarRow value={rating} onChange={setRating} />
        {!user && (
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={lang === "hi" ? "आपका नाम" : "Your name"}
            className="mt-3"
            data-testid="review-name"
          />
        )}
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={lang === "hi" ? "अपने अनुभव के बारे में लिखें…" : "Write about your experience…"}
          className="mt-2 min-h-[80px]"
          data-testid="review-comment"
        />
        <div className="flex justify-end mt-3">
          <Button
            type="submit"
            className="bg-primary hover:bg-primary/90 rounded-full"
            disabled={submitting}
            data-testid="review-submit"
          >
            {submitting && <Loader2 className="animate-spin mr-1" size={14} />}
            {lang === "hi" ? "सबमिट करें" : "Submit review"}
          </Button>
        </div>
      </form>

      {/* Reviews list */}
      {loading ? (
        <div className="text-center text-xs text-muted-foreground py-4">Loading…</div>
      ) : reviews.length === 0 ? (
        <div className="text-center text-sm text-muted-foreground py-6">
          {lang === "hi" ? "अभी तक कोई समीक्षा नहीं। पहले बनें!" : "No reviews yet. Be the first!"}
        </div>
      ) : (
        <ul className="space-y-3" data-testid="reviews-list">
          {reviews.map((r) => (
            <li key={r.review_id} className="p-3 rounded-lg border border-border/60 bg-background">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-primary/15 text-primary grid place-items-center text-xs font-bold">
                    {(r.reviewer_name || "A")[0]?.toUpperCase()}
                  </div>
                  <span className="text-sm font-semibold">{r.reviewer_name || "Anonymous"}</span>
                </div>
                <StarDisplay value={r.rating} />
              </div>
              <p className="text-sm mt-2 text-foreground/90">{r.comment}</p>
              <div className="text-[10px] text-muted-foreground mt-1">
                {new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
